import {
  HeroRoot,
  HeroContainer,
  CopyColumn,
  ImageWrapper,
  Title,
  Description,
  CallToAction,
  Feature,
  FeaturesContainer,
  ImageColumn,
  Span,
  FeatureText,
} from './RecipeDetailHero.styles'
import { useTranslation } from 'hooks/useTranslation'
import { Text } from 'components/Text'
import { Icon } from 'components/Icon'
import { Button } from 'components/Button'
import Image, { StaticImageData } from 'next/image'
import {
  InformationCollapse,
  InformationCollapseType,
} from 'components/InformationCollapse'
import Link from 'next/link'
import { TranslationKeys } from 'locales/translationKeys'
import { Nutrition } from 'models/Nutrition'
import { nutritionFormatter } from 'features/Recipes/utils/nutritionFormatter'
import { Stack } from '../../../../../components/Stack'
import Trans from 'next-translate/Trans'
import { Tooltip } from 'components/Tooltip'

type HeroFeature = {
  certification: TranslationKeys
  disclaimer?: TranslationKeys
}

export type RecipeDetailHeroProps = {
  imageSrc: StaticImageData
  imageAlt: TranslationKeys
  title: TranslationKeys
  features: HeroFeature[]
  description: TranslationKeys
  infoList: InformationCollapseType[]
  nutrition: Nutrition
  proteinPricing: TranslationKeys
}

export const RecipeDetailHero = ({
  imageSrc,
  imageAlt,
  title,
  features,
  description,
  infoList,
  nutrition,
  proteinPricing,
}: RecipeDetailHeroProps) => {
  const { t } = useTranslation()
  const formattedNutrition = nutritionFormatter(nutrition)

  return (
    <HeroRoot>
      <HeroContainer>
        <CopyColumn>
          <Title>{t(title)}</Title>
          <FeaturesContainer>
            {features.map(({ certification, disclaimer }, index) => (
              <Feature key={index}>
                <Icon size="4" color="primary-500" name="checkbox-circle-line" />
                <FeatureText>
                  <Text size="caption">{t(certification)}</Text>
                  {disclaimer && (
                    <Tooltip instant side="top" text={t(disclaimer)}>
                      <Stack alignItems="center" justifyContent="center">
                        <Icon size="3" name="information-circle-line" color="accent-black" />
                      </Stack>
                    </Tooltip>
                  )}
                </FeatureText>
              </Feature>
            ))}
          </FeaturesContainer>
          <Description>{t(description)}</Description>
          <CallToAction>
            {t('recipes:hero-v2.cta.text')}{' '}
            <Trans i18nKey={proteinPricing} components={{ Span: <Span /> }} />
          </CallToAction>
          <Link passHref href="/meal-plan">
            <Button css={{ width: '330px', display: 'none', '@bp2': { display: 'block' } }}>
              {t('recipes:hero-v2.cta.button')}
            </Button>
          </Link>
          <InformationCollapse
            transValues={formattedNutrition}
            className="d-only"
            v2
            infoList={infoList}
          />
        </CopyColumn>
        <ImageColumn>
          <ImageWrapper>
            <Image layout="fill" objectFit="cover" priority src={imageSrc} alt={t(imageAlt)} />
          </ImageWrapper>
        </ImageColumn>
        <InformationCollapse
          transValues={formattedNutrition}
          className="m-only"
          v2
          infoList={infoList}
        />
      </HeroContainer>
    </HeroRoot>
  )
}

RecipeDetailHero.displayName = 'RecipeDetailHero'
